import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCalendar, FaSave, FaTimes, FaGlobe, FaGift } from 'react-icons/fa';
import { specialDayService } from '../../services/specialDayService';
import { languageService } from '../../services/languageService';

const SpecialDaysManagement = () => {
  const [specialDays, setSpecialDays] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formData, setFormData] = useState({
    title: {},
    items: [{ text: {} }],
    year: new Date().getFullYear(),
    month: 1,
    languages: [],
    isActive: true
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
    fetchSpecialDays();
    fetchLanguages();
  }, [selectedYear]);

  const fetchSpecialDays = async () => {
    try {
      setLoading(true);
      const response = await specialDayService.getSpecialDays({ year: selectedYear });
      setSpecialDays(response.data || []);
    } catch (error) {
      console.error('Error fetching special days:', error);
      alert('Error loading special days. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await languageService.getLanguages();
      setLanguages(response.data || []);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const handleEdit = (day) => {
    setEditingDay(day);
    setFormData({
      title: day.title || {},
      items: day.items && day.items.length > 0 ? day.items : [{ text: {} }],
      year: day.year || new Date().getFullYear(),
      month: day.month,
      languages: day.languages || [],
      isActive: day.isActive
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this special day?')) {
      try {
        await specialDayService.deleteSpecialDay(id);
        fetchSpecialDays();
        alert('Special day deleted successfully');
      } catch (error) {
        console.error('Error deleting special day:', error);
        alert('Error deleting special day. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDay) {
        await specialDayService.updateSpecialDay(editingDay._id, formData);
        alert('Special day updated successfully');
      } else {
        await specialDayService.createSpecialDay(formData);
        alert('Special day created successfully');
      }
      setShowForm(false);
      setEditingDay(null);
      resetForm();
      fetchSpecialDays();
    } catch (error) {
      console.error('Error saving special day:', error);
      alert('Error saving special day. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: {},
      items: [{ text: {} }],
      year: new Date().getFullYear(),
      month: 1,
      languages: [],
      isActive: true
    });
  };

  const handleTitleChange = (langCode, value) => {
    setFormData(prev => ({
      ...prev,
      title: {
        ...prev.title,
        [langCode]: value
      }
    }));
  };

  const handleItemTextChange = (itemIndex, langCode, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        text: {
          ...newItems[itemIndex].text,
          [langCode]: value
        }
      };
      return {
        ...prev,
        items: newItems
      };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { text: {} }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const handleLanguageToggle = (langCode) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(langCode)
        ? prev.languages.filter(l => l !== langCode)
        : [...prev.languages, langCode]
    }));
  };

  const getMonthName = (monthNum) => {
    return months.find(m => m.value === monthNum)?.label || monthNum;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Special Days Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage special days that appear in the calendar</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Filter by Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="min-w-[6rem] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {(() => {
                const currentYear = new Date().getFullYear();
                const years = [];
                for (let year = currentYear - 5; year <= currentYear + 10; year++) {
                  years.push(year);
                }
                return years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ));
              })()}
            </select>
          </div>
          <button
            onClick={() => {
              resetForm();
              setEditingDay(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaPlus /> Add Special Day
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <div className="fixed left-[50%] top-[50%] z-50 flex flex-col w-full max-w-5xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] border bg-background shadow-lg duration-200 sm:rounded-lg">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold leading-none tracking-tight">
                  {editingDay ? 'Edit Special Days' : 'Create Special Days'}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Configure accordion for the month</p>
              </div>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => {
                  setShowForm(false);
                  setEditingDay(null);
                }}
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="space-y-6 p-6">
                {/* Year, Month & Languages */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <FaCalendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <h4 className="text-sm font-medium">Year, Month & Languages</h4>
                      <p className="text-xs text-muted-foreground">Select the year, month and available languages</p>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <label className="label">Year *</label>
                      <select
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                        className="select"
                        required
                      >
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          const years = [];
                          for (let year = currentYear - 5; year <= currentYear + 10; year++) {
                            years.push(year);
                          }
                          return years.map(year => (
                            <option key={year} value={year}>{year}</option>
                          ));
                        })()}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="label">Month *</label>
                      <select
                        value={formData.month}
                        onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                        className="select"
                        required
                      >
                        {months.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="label">Active Status</label>
                      <label className="flex items-center gap-2 cursor-pointer h-10">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">Show in calendar</span>
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="label">Languages *</label>
                    <div className="flex flex-wrap gap-2">
                      {languages.map(lang => (
                        <label
                          key={lang.code}
                          className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-accent transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.languages.includes(lang.code)}
                            onChange={() => handleLanguageToggle(lang.code)}
                            className="rounded"
                          />
                          <span className="text-sm">{lang.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Accordion Title */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <FaGlobe className="h-5 w-5 text-primary" />
                    <div>
                      <h4 className="text-base font-semibold text-foreground">Accordion Header Title</h4>
                      <p className="text-xs text-muted-foreground">Enter the accordion title in multiple languages</p>
                    </div>
                  </div>
                  <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {languages.map((lang) => (
                      <div key={lang.code} className="relative">
                        <div className="card p-4 hover:shadow-sm transition-shadow border border-border">
                          <label className="block mb-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-foreground">
                                {lang.name}
                              </span>
                              <span className="text-xs text-muted-foreground font-medium">{lang.nativeName}</span>
                            </div>
                          </label>
                          <input
                            type="text"
                            className="input w-full"
                            value={formData.title[lang.code] || ''}
                            onChange={(e) => handleTitleChange(lang.code, e.target.value)}
                            placeholder={`Title in ${lang.name}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items (Pills) */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <FaGift className="h-5 w-5 text-primary" />
                      <div>
                        <h4 className="text-base font-semibold text-foreground">Special Day Items</h4>
                        <p className="text-xs text-muted-foreground">Add items that will appear as pills in the accordion</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={addItem}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <FaPlus /> Add Item
                    </button>
                  </div>

                  <div className="space-y-4">
                    {formData.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="card p-4 border border-border">
                        <div className="flex justify-between items-center mb-4">
                          <h5 className="text-sm font-semibold text-foreground">Item {itemIndex + 1}</h5>
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(itemIndex)}
                              className="btn btn-ghost btn-sm text-destructive hover:bg-destructive/10"
                              title="Remove this item"
                            >
                              <FaTrash /> Remove
                            </button>
                          )}
                        </div>
                        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                          {languages.map(lang => (
                            <div key={lang.code}>
                              <label className="block mb-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-muted-foreground">{lang.name}</span>
                                  <span className="text-xs text-muted-foreground">{lang.nativeName}</span>
                                </div>
                              </label>
                              <input
                                type="text"
                                className="input w-full"
                                value={item.text[lang.code] || ''}
                                onChange={(e) => handleItemTextChange(itemIndex, lang.code, e.target.value)}
                                placeholder={`Text in ${lang.name}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingDay(null);
                  }}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex items-center gap-2"
                >
                  <FaSave /> {editingDay ? 'Update Special Days' : 'Create Special Days'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Special Days List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading special days...</p>
        </div>
      ) : specialDays.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FaCalendar className="mx-auto text-gray-400 text-5xl mb-4" />
          <p className="text-gray-600">No special days found. Add your first special day!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {specialDays.map(day => (
            <div
              key={day._id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-800">
                    {day.title?.en || Object.values(day.title)[0] || 'Untitled'}
                  </h3>
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <FaCalendar className="text-blue-500" />
                    {getMonthName(day.month)} {day.year}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(day)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(day._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              {day.items && day.items.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {day.items.map((item, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full border border-gray-300"
                    >
                      {item.text?.en || Object.values(item.text || {})[0] || 'No text'}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-1">
                {day.languages?.map(lang => (
                  <span
                    key={lang}
                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                  >
                    {languages.find(l => l.code === lang)?.name || lang}
                  </span>
                ))}
              </div>

              {!day.isActive && (
                <div className="mt-2 text-xs text-red-600 font-medium">
                  Inactive
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpecialDaysManagement;
